import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { CompletedCompetitionsGrid } from './components/CompletedCompetitionsGrid';
import { ContentWithSidebar } from '../../../../../components/ContentWithSidebar';
import { PageTitle } from '../../../../../components/PageTitle';
import { Advertisement } from '@components/content-side-bar';
import { useGetPortalCompletedCompetitions } from '@reactQuery/competitions';
import { mapPortalCompletedCompetitionToCard } from '../../helpers';
import { Loader } from '@components/feed/Loader';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@routes/routes';

export const ResultsOverview: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetPortalCompletedCompetitions({ perPage: 12 });

  const competitions = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map((competition) =>
          mapPortalCompletedCompetitionToCard(
            competition,
            ROUTES.portal.competitions.results.details(competition.slug)
          )
        )
      ) ?? [],
    [data]
  );

  useEffect(() => {
    if (!loaderRef.current || !hasNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  return (
    <ContentWithSidebar sidebarContent={<Advertisement />}>
      <PageTitle
        title={t('competitions:portal.results.overview.title')}
        subTitle={t('competitions:portal.results.overview.subTitle')}
      />

      {!isLoading && competitions.length > 0 && (
        <CompletedCompetitionsGrid competitions={competitions} />
      )}

      {!isLoading && competitions.length === 0 && (
        <EmptyPlaceholder
          icon={EmojiEventsIcon}
          title={t(
            'competitions:portal.results.overview.emptyPlaceholders.noCompetitions.title'
          )}
          subTitle={t(
            'competitions:portal.results.overview.emptyPlaceholders.noCompetitions.subTitle'
          )}
        />
      )}

      {(hasNextPage || isLoading) && (
        <Box ref={loaderRef} py={2} minHeight={40}>
          {(isLoading || isFetchingNextPage) && <Loader />}
        </Box>
      )}
    </ContentWithSidebar>
  );
};
